<template>
	<main class="space-y-section" :aria-label="blok.title ?? 'Letáky'">
		<section class="container !pt-0">
			<div v-if="!selectedHypermarket" class="mb-10">
				<div class="typo-h1">
					Akční letáky a katalogy
				</div>
				<p class="typo-body-m mb-10">
					Výběrem místa zobrazíte všechny aktuálně platné i nadcházející letáky a katalogy dané pobočky.
				</p>
				<HypermarketSelectionBanner hide-header-icon show-links />
			</div>
			<LazyPageHeadingWithHypermarket
				class="mb-10"
				:heading="selectedHypermarket ? 'Akční letáky a katalogy' : 'Aktuální letáky a katalogy'"
				information-text="Informace o letácích se vztahují k"
				:default-hm="DEFAULT_HYPERMARKET"
				show-links-in-selection-banner
			>
				<template #aside>
					<div class="flex items-center gap-2 font-bold order-2 lg:order-1">
						<NuxtIcon name="bell" class="text-xl" filled />
						<span class="typo-body-m">
							Každou středu nový leták
						</span>
					</div>
				</template>
			</LazyPageHeadingWithHypermarket>

			<nav class="overflow-x-auto rounded-xl md:rounded-none shadow-md md:shadow-none border border-border-tertiary md:border-0 md:border-transparent mb-8" aria-label="Záložky s akčními letáky">
				<HorizontalScroll
					class="px-4 md:px-0 flex gap-2 [--fade-color:var(--color-neutral-300)]"
					role="tablist"
					fade-edges
				>
					<li v-for="item in tabs" :key="item.id" role="tab" class="inline-flex">
						<UiTabToggle
							:id="slugify(item.label, {lower: true})"
							:key="item.id"
							:as="AbsoluteInternalLink"
							:to="item.hash"
							:active="activeTab === item.id"
							@click.prevent="handleTabSelected(item.id)"
						>
							{{ item.label }} ({{ item.count }})
						</UiTabToggle>
					</li>
				</HorizontalScroll>
			</nav>

			<LeafletListingSkeleton v-if="status === 'pending'" />

			<template v-else-if="status === 'success' && validAllActionOffers">
				<div v-if="visibleSelectedTab.length">
					<TransitionGroup
						:key="houseNumber ?? 'globus'"
						name="fade"
						tag="div"
						role="list"
						class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-2 gap-y-6 sm:gap-x-4 md:gap-x-6"
					>
						<LeafletTile
							v-for="(leaflet, index) in visibleSelectedTab"
							:key="leaflet.slug ?? `leaflet-${index}`"
							:section="blok.title ?? 'Letáky'"
							:counter="computerToHumanIndexing(index)"
							:leaflet="leaflet"
							:current-main-leaflet-id="currentMainLeafletId"
							role="listitem"
						/>
					</TransitionGroup>
				</div>
				<div v-else>
					<div class="text-center my-16">
						<NuxtIcon name="frown" filled class="text-7xl mb-4 text-icon-primary text-center inline-block" />
						<h3 class="typo-h4 text-heading-primary mb-6">
							Bohužel jsme nenašli žádné letáky
						</h3>
					</div>
				</div>
			</template>
		</section>

		<ActionOfferProductListing :title="`Akční nabídka ${selectedHypermarketWithFallbackInstance?.content.name}`" />

		<StoryblokComponent
			v-if="newsletterBanner"
			:id="newsletterBanner.slug"
			v-editable="blok"
			:blok="newsletterBanner.content"
		/>

		<StoryblokComponent
			v-if="followUpTiles"
			:blok="followUpTiles"
		/>
	</main>
</template>

<script setup lang="ts">
	import slugify from 'slugify';
	import {useRouteHash} from '@vueuse/router';
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import useSeoSettings from '~/composables/useSeoSettings';
	import type {PageTypeLeafletListingStoryblok} from '@/storyblok/types';
	import {GsoaActionOffersService} from '~/openapi';
	import {DEFAULT_HYPERMARKET, FOLLOW_UP_TILES_SLUG} from '#shared/constants';
	import {computerToHumanIndexing} from '~/utils';

	interface Props {
		blok: PageTypeLeafletListingStoryblok;
	}

	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');
	const props = defineProps<Props>();
	const newsletterBanner = computed(() => resolvedStory(props.blok.newsletterSignupBanner));
	const routeHash = useRouteHash();

	const tabs = computed(() => [
		{
			id: 0,
			label: 'Všechny',
			count: validAllActionOffers.value?.length ?? 0,
			hash: `#${slugify('Všechny', {lower: true})}`,
		},
		{
			id: 1,
			label: 'Aktuální',
			count: validActiveActionOffers.value?.length ?? 0,
			hash: `#${slugify('Aktuální', {lower: true})}`,
		},
		{
			id: 2,
			label: 'Nadcházející',
			count: validComingActionOffers.value?.length ?? 0,
			hash: `#${slugify('Nadcházející', {lower: true})}`,
		},
	] as const);

	const activeTab = ref(0);
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const houseNumber = computed(
		() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId,
	);

	const validActiveActionOffers = computed(() => getActiveActionOffersList(leaflets.value?.actionOffersDocuments ?? []));
	const validComingActionOffers = computed(() => getComingActionOffersList(leaflets.value?.actionOffersDocuments ?? []));
	const validAllActionOffers = computed(() => leaflets.value?.actionOffersDocuments ?? []);
	const currentMainLeafletId = computed(() => getCurrentMainLeaflet(validAllActionOffers.value)?.actionOfferId ?? null);

	const tabContentMappings = [
		validAllActionOffers,
		validActiveActionOffers,
		validComingActionOffers,
	];

	const visibleSelectedTab = computed(() => {
		return tabContentMappings[activeTab.value]?.value ?? [];
	});

	const {data: leaflets, status, error} = await useLazyAsyncData(
		`leaflets-page-listing-${houseNumber.value ?? 'none'}`,
		() => cancelTimeout(
			() => houseNumber.value
				? GsoaActionOffersService.getActionOffersDocuments({
					houseNumber: Number(houseNumber.value),
					orderingRule: '1',
					hideInComplete: true,
					...getLeafletsFilter(),
				})
				: Promise.resolve(null),
		),
		{watch: [houseNumber]},
	);

	if (error.value) {
		useShowError(error);
	}

	const {data: fetchedFollowUpTiles} = await useSingleStoryFetch(
		FOLLOW_UP_TILES_SLUG,
		undefined,
		{
			lazy: true,
			server: true,
			immediate: !resolvedStory(props.blok.followUpTiles)?.content,
		},
	);

	const followUpTiles = computed(() => {
		const cached = resolvedStory(props.blok.followUpTiles)?.content;
		if (cached) {
			return cached;
		}
		return fetchedFollowUpTiles.value?.content;
	});

	onMounted(() => {
		const hashTab = tabs.value.find((item) => item.hash === routeHash.value);
		if (hashTab) {
			activeTab.value = hashTab.id;
		}
	});

	// Sync activeTab with route hash changes - Assistive24 chrome extension changes hash on tab click
	watch(routeHash, (newHash) => {
		const hashTab = tabs.value.find((item) => item.hash === newHash);
		if (hashTab) {
			activeTab.value = hashTab.id;
		}
	});

	async function handleTabSelected(index: number) {
		await navigateTo({hash: `${tabs.value[index].hash}`});
		activeTab.value = index;
	}

	useSeoSettings(props.blok.seoSettings?.[0]);
</script>
